import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, AlertTriangle, RefreshCw, Database, FileText, Trash2, History } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { getErrorMessage } from '@/utils/errorHelpers';

const TenantDataReset = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetOptions, setResetOptions] = useState({
    opinions: false,
    documents: false,
    extractions: false,
    wallet: false,
    transactions: false,
    all: false
  });
  const [reason, setReason] = useState('');
  const [createBackup, setCreateBackup] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [resetHistory, setResetHistory] = useState([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  useEffect(() => {
    fetchTenants();
    fetchResetHistory();
  }, []);

  const fetchTenants = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/tenants/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setTenants(response.data.tenants || []);
    } catch (error) {
      console.error('Error fetching tenants:', error);
      toast.error(getErrorMessage(error, 'Failed to load tenants'));
    } finally {
      setLoading(false);
    }
  };

  const fetchResetHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/tenant-reset/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setResetHistory(response.data.history || []);
    } catch (error) {
      console.error('Error fetching reset history:', error);
    }
  };

  const handleResetClick = (tenant) => {
    if (tenant.id === 'default') {
      toast.error('Cannot reset default tenant');
      return;
    }
    setSelectedTenant(tenant);
    setShowResetModal(true);
    setResetOptions({
      opinions: false,
      documents: false,
      extractions: false,
      wallet: false,
      transactions: false,
      all: false
    });
    setReason('');
  };

  const handleOptionChange = (option) => {
    if (option === 'all') {
      const allSelected = !resetOptions.all;
      setResetOptions({
        opinions: allSelected,
        documents: allSelected,
        extractions: allSelected,
        wallet: allSelected,
        transactions: allSelected,
        all: allSelected
      });
    } else {
      setResetOptions({
        ...resetOptions,
        [option]: !resetOptions[option],
        all: false
      });
    }
  };

  const handleReset = async () => {
    if (!reason.trim()) {
      toast.error('Please provide a reason for reset');
      return;
    }

    const selectedOptions = Object.keys(resetOptions).filter(key => resetOptions[key]);
    if (selectedOptions.length === 0) {
      toast.error('Please select at least one option to reset');
      return;
    }

    setResetting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/tenant-reset/reset`,
        {
          tenant_id: selectedTenant.id,
          reset_options: selectedOptions,
          reason: reason.trim(),
          create_backup: createBackup
        },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      toast.success('Tenant data reset successfully!');
      setShowResetModal(false);
      fetchResetHistory();
      
      // Show summary
      const summary = response.data.summary;
      console.log('Reset Summary:', summary);
    } catch (error) {
      console.error('Reset error:', error);
      toast.error(getErrorMessage(error, 'Failed to reset tenant data'));
    } finally {
      setResetting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin')}
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Admin</span>
            </Button>
            <h1 className="text-3xl font-bold text-gray-900">Tenant Data Reset</h1>
          </div>
          
          <Button
            onClick={() => setShowHistoryModal(true)}
            className="flex items-center space-x-2"
          >
            <History className="w-4 h-4" />
            <span>Reset History</span>
          </Button>
        </div>

        {/* Warning Banner */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <div className="flex">
            <AlertTriangle className="w-5 h-5 text-yellow-400 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-yellow-800">Warning: Data Reset</h3>
              <p className="text-sm text-yellow-700 mt-1">
                Resetting tenant data will permanently delete selected data. This action is used for starting fresh billing cycles. 
                A backup will be created unless explicitly disabled.
              </p>
            </div>
          </div>
        </div>

        {/* Tenants List */}
        <Card>
          <CardHeader>
            <CardTitle>Select Tenant to Reset</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tenants.map((tenant) => (
                <div
                  key={tenant.id}
                  className={`border rounded-lg p-4 ${
                    tenant.id === 'default' 
                      ? 'bg-gray-100 border-gray-300 cursor-not-allowed' 
                      : 'bg-white border-gray-200 hover:border-blue-400 hover:shadow-md cursor-pointer'
                  }`}
                  onClick={() => tenant.id !== 'default' && handleResetClick(tenant)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{tenant.name}</h3>
                      <p className="text-sm text-gray-500 mt-1">{tenant.id}</p>
                      <div className="mt-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          tenant.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {tenant.status || 'active'}
                        </span>
                      </div>
                    </div>
                    {tenant.id !== 'default' && (
                      <Trash2 className="w-5 h-5 text-red-500" />
                    )}
                  </div>
                  {tenant.id === 'default' && (
                    <p className="text-xs text-gray-500 mt-2">Cannot reset default tenant</p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Reset Modal */}
        <Dialog open={showResetModal} onOpenChange={setShowResetModal}>
          <DialogContent className="bg-white max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center space-x-2">
                <RefreshCw className="w-5 h-5 text-red-600" />
                <span>Reset Tenant Data: {selectedTenant?.name}</span>
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Warning */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex">
                  <AlertTriangle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-red-800">Irreversible Action</h4>
                    <p className="text-sm text-red-700 mt-1">
                      Selected data will be permanently deleted. This action cannot be undone unless you restore from backup.
                    </p>
                  </div>
                </div>
              </div>

              {/* Reset Options */}
              <div>
                <Label className="text-base font-semibold mb-3 block">Select Data to Reset:</Label>
                <div className="space-y-2">
                  <label className="flex items-center space-x-3 p-3 border rounded hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={resetOptions.all}
                      onChange={() => handleOptionChange('all')}
                      className="w-4 h-4 text-blue-600"
                    />
                    <div className="flex-1">
                      <span className="font-medium text-red-600">Reset All Data</span>
                      <p className="text-xs text-gray-500">Delete everything except tenant account</p>
                    </div>
                  </label>

                  <div className="border-t pt-2 space-y-2">
                    <label className="flex items-center space-x-3 p-3 border rounded hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={resetOptions.opinions}
                        onChange={() => handleOptionChange('opinions')}
                        className="w-4 h-4 text-blue-600"
                      />
                      <div className="flex-1">
                        <span className="font-medium">Legal Opinions</span>
                        <p className="text-xs text-gray-500">Delete all generated opinions</p>
                      </div>
                    </label>

                    <label className="flex items-center space-x-3 p-3 border rounded hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={resetOptions.documents}
                        onChange={() => handleOptionChange('documents')}
                        className="w-4 h-4 text-blue-600"
                      />
                      <div className="flex-1">
                        <span className="font-medium">Documents</span>
                        <p className="text-xs text-gray-500">Delete all uploaded documents</p>
                      </div>
                    </label>

                    <label className="flex items-center space-x-3 p-3 border rounded hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={resetOptions.extractions}
                        onChange={() => handleOptionChange('extractions')}
                        className="w-4 h-4 text-blue-600"
                      />
                      <div className="flex-1">
                        <span className="font-medium">Extractions</span>
                        <p className="text-xs text-gray-500">Delete all EC extractions</p>
                      </div>
                    </label>

                    <label className="flex items-center space-x-3 p-3 border rounded hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={resetOptions.wallet}
                        onChange={() => handleOptionChange('wallet')}
                        className="w-4 h-4 text-blue-600"
                      />
                      <div className="flex-1">
                        <span className="font-medium">Wallet Balance</span>
                        <p className="text-xs text-gray-500">Reset credit balance to 0</p>
                      </div>
                    </label>

                    <label className="flex items-center space-x-3 p-3 border rounded hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={resetOptions.transactions}
                        onChange={() => handleOptionChange('transactions')}
                        className="w-4 h-4 text-blue-600"
                      />
                      <div className="flex-1">
                        <span className="font-medium">Transaction History</span>
                        <p className="text-xs text-gray-500">Delete all wallet transactions</p>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              {/* Backup Option */}
              <div>
                <label className="flex items-center space-x-3 p-3 bg-blue-50 border border-blue-200 rounded cursor-pointer">
                  <input
                    type="checkbox"
                    checked={createBackup}
                    onChange={(e) => setCreateBackup(e.target.checked)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <div className="flex-1">
                    <span className="font-medium text-blue-900">Create Backup Before Reset</span>
                    <p className="text-xs text-blue-700">Recommended - allows data restoration if needed</p>
                  </div>
                </label>
              </div>

              {/* Reason */}
              <div>
                <Label htmlFor="reason" className="required">Reason for Reset *</Label>
                <Textarea
                  id="reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g., Starting new billing cycle, Demo reset, etc."
                  rows={3}
                  className="mt-2"
                  required
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowResetModal(false)}
                disabled={resetting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleReset}
                disabled={resetting}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {resetting ? 'Resetting...' : 'Confirm Reset'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* History Modal */}
        <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
          <DialogContent className="bg-white max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Reset History</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              {resetHistory.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No reset history found</p>
              ) : (
                <div className="space-y-4">
                  {resetHistory.map((entry, index) => (
                    <div key={index} className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">{entry.tenant_name}</h4>
                          <p className="text-sm text-gray-600 mt-1">Tenant ID: {entry.tenant_id}</p>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {entry.reset_options.map((option, i) => (
                              <span key={i} className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">
                                {option}
                              </span>
                            ))}
                          </div>
                          <p className="text-sm text-gray-600 mt-2">
                            <strong>Reason:</strong> {entry.reason}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Reset by: {entry.reset_by} • {formatDate(entry.timestamp)}
                          </p>
                          {entry.backup_id && (
                            <p className="text-xs text-blue-600 mt-1">
                              Backup ID: {entry.backup_id}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default TenantDataReset;
